import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Heart, MessageCircle, ArrowLeft, Ban, Handshake, PenSquare, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { PageHeader, btn, Loading, ErrorBox, EmptyState, Badge, ReportButton, SaveButton, TextField, TextArea, SelectField, ErrorSummary, Pagination } from "../components/kit";
import { SearchBar } from "../components/Listing";
import { api, errMsg, useFetch, label, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TYPES = ["discussion", "question", "tip", "update", "recruitment", "mentorship", "resource", "event"];
const BASIS = [["personal_experience", "Personal experience"], ["professional_opinion", "Professional opinion"], ["verified_fact", "Verified fact (with source)"]];
const basisLabel = (b) => BASIS.find((x) => x[0] === b)?.[1];

function NewPost({ cats, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", body: "", category: "", type: "discussion", content_basis: "personal_experience" });
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.title.trim().length < 5) er["post-title"] = "Write a title of at least 5 characters.";
    if (f.body.trim().length < 10) er["post-body"] = "Write at least 10 characters.";
    if (!f.category) er["post-category"] = "Choose a category.";
    setErrors(er); setServerErr("");
    if (Object.keys(er).length) return;
    try { await api.post("/posts", f); toast.success("Post published"); setOpen(false); setF({ ...f, title: "", body: "" }); onCreated(); }
    catch (e2) { setServerErr(errMsg(e2)); }
  };
  return (
    <>
      <button className={btn("community")} onClick={() => setOpen(true)} data-testid="create-post-btn"><PenSquare className="h-5 w-5" aria-hidden="true" />Start a discussion</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl bg-white" data-testid="create-post-dialog">
          <DialogHeader><DialogTitle className="text-2xl text-navy">New post</DialogTitle><DialogDescription>Please follow the <Link to="/guidelines" className="link">community guidelines</Link>. Never share anyone's personal or medical information.</DialogDescription></DialogHeader>
          <form onSubmit={submit} noValidate className="space-y-4">
            <ErrorSummary errors={errors} />
            <TextField id="post-title" label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} error={errors["post-title"]} />
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField id="post-category" label="Category" required value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Choose" options={cats} error={errors["post-category"]} />
              <SelectField id="post-type" label="Post type" required value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} options={TYPES.map((t) => [t, label(t)])} />
            </div>
            <SelectField id="post-basis" label="This post is based on" required hint="Helps readers tell experiences, opinions and facts apart." value={f.content_basis} onChange={(e) => setF({ ...f, content_basis: e.target.value })} options={BASIS} />
            <TextArea id="post-body" label="Post" required rows={7} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} error={errors["post-body"]} />
            {serverErr && <ErrorBox message={serverErr} />}
            <div className="flex justify-end gap-3"><button type="button" className={btn("secondary")} onClick={() => setOpen(false)}>Cancel</button><button className={btn("primary")} data-testid="post-submit-btn">Publish</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LikeButton({ post }) {
  const { user } = useAuth();
  const [s, setS] = useState({ liked: post.liked, count: post.like_count });
  const toggle = async () => {
    if (!user) { toast.info("Sign in to react to posts."); return; }
    try { const { data } = await api.post(`/posts/${post.id}/like`); setS({ liked: data.liked, count: data.like_count }); } catch (e) { toast.error(errMsg(e)); }
  };
  return <button onClick={toggle} aria-pressed={s.liked} className={btn("ghost", "sm")} data-testid={`like-post-${post.id}`}><Heart className={`h-4 w-4 ${s.liked ? "fill-current" : ""}`} aria-hidden="true" />{s.liked ? "Liked" : "Like"} ({s.count})</button>;
}

function PostCard({ p }) {
  return (
    <article className="card p-5" data-testid={`post-card-${p.id}`}>
      <div className="flex flex-wrap gap-2"><Badge tone="purple">{p.category}</Badge><Badge tone="grey">{label(p.type)}</Badge><Badge tone="blue">{basisLabel(p.content_basis)}</Badge></div>
      <h3 className="mt-3 text-xl font-bold"><Link to={`/community/posts/${p.id}`} className="hover:underline underline-offset-4">{p.title}</Link></h3>
      <p className="mt-1 text-sm text-sub">{p.author_name}{p.author_headline && ` · ${p.author_headline}`} · {fmtDate(p.created_at)}</p>
      <p className="mt-3 line-clamp-3">{p.body}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <LikeButton post={p} />
        <Link to={`/community/posts/${p.id}`} className={btn("ghost", "sm")}><MessageCircle className="h-4 w-4" aria-hidden="true" />{p.comment_count} replies<span className="sr-only"> on {p.title}</span></Link>
      </div>
    </article>
  );
}

export function CommunityFeed() {
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const meta = useFetch("/meta");
  const cat = sp.get("category") || "";
  const q = sp.get("q") || "";
  const page = Number(sp.get("page") || 1);
  const feed = useFetch(`/posts?${new URLSearchParams({ category: cat, q, page })}`);
  const setParam = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setSp(n); };
  const cats = meta.data?.community_categories || [];
  return (
    <>
      <PageHeader
        eyebrow="Community"
        title="OpenPath Community"
        description="Career advice, questions and answers, resources and events — moderated and privacy-conscious. You never need to share disability status to take part."
      >
        {user ? <NewPost cats={cats} onCreated={feed.reload} /> : <Link to="/signin?next=/community" className={btn("community")} data-testid="signin-to-post">Sign in to post</Link>}
        <Link to="/community/directory" className={btn("secondary")} data-testid="directory-link">Professional directory</Link>
      </PageHeader>
      <div className="container-x py-8 grid gap-8 lg:grid-cols-[260px_1fr]">
        <nav aria-label="Community categories">
          <h2 className="text-lg font-bold">Categories</h2>
          <ul className="mt-3 space-y-1">
            {["", ...cats].map((c) => (
              <li key={c || "all"}><button onClick={() => setParam("category", c)} aria-current={cat === c ? "true" : undefined} className={`w-full min-h-[44px] rounded-lg px-3 text-left font-medium ${cat === c ? "bg-purple/10 text-purple border-l-4 border-purple" : "hover:bg-mist"}`} data-testid={`category-${c || "all"}`}>{c || "All categories"}</button></li>
            ))}
          </ul>
        </nav>
        <section aria-labelledby="feed-h">
          <h2 id="feed-h" className="sr-only">Posts</h2>
          <SearchBar value={q} onSearch={(v) => setParam("q", v)} placeholder="Search discussions" labelText="Search community posts" />
          <p className="mt-4 font-semibold" aria-live="polite" data-testid="feed-count">{feed.data && `${feed.data.total} posts${cat ? ` in ${cat}` : ""}`}</p>
          <div className="mt-4 space-y-4">
            {feed.loading ? <Loading /> : feed.error ? <ErrorBox message={feed.error} onRetry={feed.reload} /> : feed.data.items.length ? feed.data.items.map((p) => <PostCard key={p.id} p={p} />) : <EmptyState icon={MessagesSquare} title="No posts yet" body="Start the first conversation in this category." />}
          </div>
          {feed.data && <Pagination page={feed.data.page} pages={feed.data.pages} onChange={(p) => setParam("page", p)} />}
        </section>
      </div>
    </>
  );
}

function CommentForm({ postId, parentId, onDone, compact }) {
  const [body, setBody] = useState("");
  const [err, setErr] = useState("");
  const id = `comment-${parentId || "root"}`;
  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 2) { setErr("Write a reply before posting."); return; }
    try { await api.post(`/posts/${postId}/comments`, { body, parent_id: parentId || null }); setBody(""); setErr(""); toast.success("Reply posted"); onDone(); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-3" data-testid={`${id}-form`}>
      <TextArea id={id} label={parentId ? "Your reply" : "Add a comment"} rows={compact ? 2 : 3} required value={body} onChange={(e) => setBody(e.target.value)} error={err} />
      <button className={btn("primary", "sm")} data-testid={`${id}-submit`}>{parentId ? "Post reply" : "Post comment"}</button>
    </form>
  );
}

function threadOrder(all) {
  const out = [];
  const walk = (parent, depth) => all.filter((x) => (x.parent_id || null) === parent).forEach((c) => { out.push({ c, depth, parent: all.find((p) => p.id === c.parent_id) }); walk(c.id, depth + 1); });
  walk(null, 0);
  return out;
}

function Comment({ c, depth, parent, postId, reload }) {
  const { user } = useAuth();
  const [replying, setReplying] = useState(false);
  return (
    <li className="border-l-2 border-line pl-4" style={{ marginLeft: `${Math.min(depth, 4) * 1.5}rem` }} data-testid={`comment-${c.id}`}>
      <p className="text-sm font-semibold">{c.author_name} <span className="font-normal text-sub">· {fmtDate(c.created_at)}{parent && ` · replying to ${parent.author_name}`}</span></p>
      <p className="mt-1 whitespace-pre-line">{c.body}</p>
      <div className="flex flex-wrap gap-1">
        {user && <button className={btn("ghost", "sm")} onClick={() => setReplying(!replying)} aria-expanded={replying} data-testid={`reply-btn-${c.id}`}>Reply</button>}
        <ReportButton type="comment" id={c.id} />
      </div>
      {replying && <div className="mt-2"><CommentForm postId={postId} parentId={c.id} compact onDone={() => { setReplying(false); reload(); }} /></div>}
    </li>
  );
}

export function ReferralDialog({ targetId, targetName, kind, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ company: "", role_title: "", message: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (f.message.trim().length < 20) { setErr("Write a message of at least 20 characters."); return; }
    try { await api.post("/referrals", { ...f, kind, target_user_id: targetId }); toast.success("Sent"); setOpen(false); } catch (e2) { setErr(errMsg(e2)); }
  };
  const title = kind === "request_referral" ? `Request a referral from ${targetName}` : `Offer an introduction to ${targetName}`;
  return (
    <>
      <button className={btn("secondary", "sm")} onClick={() => setOpen(true)} data-testid={`${kind}-btn`}><Handshake className="h-4 w-4" aria-hidden="true" />{trigger}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid={`${kind}-dialog`}>
          <DialogHeader><DialogTitle className="text-navy">{title}</DialogTitle><DialogDescription>Referrals and introductions are never guaranteed. Be specific and respectful.</DialogDescription></DialogHeader>
          <form onSubmit={submit} noValidate className="space-y-4">
            <TextField id="ref-company" label="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
            <TextField id="ref-role" label="Role or topic" value={f.role_title} onChange={(e) => setF({ ...f, role_title: e.target.value })} />
            <TextArea id="ref-message" label="Message" required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} error={err} />
            <div className="flex justify-end"><button className={btn()} data-testid="ref-submit-btn">Send</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data, loading, error, reload } = useFetch(`/posts/${id}`);
  const block = async () => {
    try { const { data: r } = await api.post(`/blocks/${data.post.author_id}`); toast.success(r.blocked ? "User blocked. You won't see their posts." : "User unblocked"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <div className="container-x"><Loading /></div>;
  if (error) return <div className="container-x py-10"><ErrorBox message={error} onRetry={reload} /></div>;
  const { post: p, comments } = data;
  const mine = user && user.id === p.author_id;
  return (
    <div className="container-x py-8 max-w-3xl">
      <Link to="/community" className="link inline-flex min-h-[44px] items-center gap-1"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to community</Link>
      <article className="mt-4" data-testid="post-detail">
        <div className="flex flex-wrap gap-2"><Badge tone="purple">{p.category}</Badge><Badge tone="grey">{label(p.type)}</Badge><Badge tone="blue">{basisLabel(p.content_basis)}</Badge></div>
        <h1 className="mt-3 text-3xl font-extrabold">{p.title}</h1>
        <p className="mt-2 text-sub">{p.author_name}{p.author_headline && ` · ${p.author_headline}`} · {fmtDate(p.created_at)}</p>
        <div className="mt-6 whitespace-pre-line text-lg">{p.body}</div>
        <div className="mt-6 flex flex-wrap gap-2 border-y border-line py-3">
          <LikeButton post={p} />
          <SaveButton kind="post" id={p.id} initial={data.saved} />
          <ReportButton type="post" id={p.id} />
          {user && !mine && <ReferralDialog targetId={p.author_id} targetName={p.author_name} kind="offer_introduction" trigger="Offer an introduction" />}
          {user && !mine && <button className={btn("ghost", "sm")} onClick={block} data-testid="block-author-btn"><Ban className="h-4 w-4" aria-hidden="true" />Block author</button>}
        </div>
      </article>
      <section className="mt-8" aria-labelledby="comments-h">
        <h2 id="comments-h" className="text-2xl font-bold">{comments.length} replies</h2>
        <div className="mt-4">{user ? <CommentForm postId={p.id} onDone={reload} /> : <p><Link to={`/signin?next=/community/posts/${p.id}`} className="link">Sign in</Link> to join the conversation.</p>}</div>
        <ul className="mt-6 space-y-5">{threadOrder(comments).map(({ c, depth, parent }) => <Comment key={c.id} c={c} depth={depth} parent={parent} postId={p.id} reload={reload} />)}</ul>
      </section>
    </div>
  );
}
